import {
  Box,
  Button,
  Group,
  Modal,
  NumberInput,
  Select,
  Stack,
  Text,
  Textarea,
} from "@mantine/core"
import { useForm } from "@mantine/form"
import { useTranslation } from "react-i18next"
import { IInboundOrderItem } from "../../apis/inbound-order"
import { translateBackendError } from "../delivery/translate-error"
import { useInboundExceptionMutations } from "./hooks"
import { inboundItemLabel } from "./inbound-item-label"

interface Props {
  opened: boolean
  onClose: () => void
  inboundOrderId: string | null
  items: IInboundOrderItem[]
}

export function RaiseExceptionDialog({ opened, onClose, inboundOrderId, items }: Props) {
  const { t } = useTranslation("inbound")
  const mutations = useInboundExceptionMutations()

  const EXCEPTION_TYPE_OPTIONS = [
    { value: "QUANTITY_SHORT",       label: t("raiseException.typeShort") },
    { value: "QUANTITY_OVER",        label: t("raiseException.typeOver") },
    { value: "DAMAGED",              label: t("exceptionType.DAMAGED") },
    { value: "MIXED",                label: t("exceptionType.MIXED") },
    { value: "UNCATALOGUED_PENDING", label: t("exceptionType.UNCATALOGUED_PENDING") },
  ]

  const CONDITION_OPTIONS = [
    { value: "GOOD",    label: t("condition.GOOD") },
    { value: "DAMAGED", label: t("condition.DAMAGED") },
    { value: "MIXED",   label: t("condition.MIXED") },
  ]

  const itemOptions = [
    { value: "__order__", label: t("raiseException.orderLevel") },
    ...items.map((it) => ({
      value: it.id,
      label: inboundItemLabel(it, t),
    })),
  ]

  const form = useForm({
    initialValues: {
      itemSelection: "__order__",
      exceptionType: "",
      expectedQuantity: "" as string | number,
      actualQuantity: "" as string | number,
      conditionObserved: "" as string,
      description: "",
    },
    validate: {
      exceptionType: (v) => (!v ? t("raiseException.exceptionTypeRequired") : null),
      description: (v) =>
        !v || v.trim().length < 3 ? t("raiseException.descriptionRequired") : null,
    },
  })

  const handleSubmit = async (values: typeof form.values) => {
    if (!inboundOrderId) return
    const body: any = {
      exceptionType: values.exceptionType,
      description: values.description,
    }
    if (values.itemSelection !== "__order__") {
      body.inboundOrderItemId = values.itemSelection
    }
    if (values.expectedQuantity !== "") body.expectedQuantity = Number(values.expectedQuantity)
    if (values.actualQuantity !== "") body.actualQuantity = Number(values.actualQuantity)
    if (values.conditionObserved) body.conditionObserved = values.conditionObserved

    try {
      await mutations.raise(inboundOrderId, body)
      form.reset()
      onClose()
    } catch {
      // error surfaced via mutations.error
    }
  }

  return (
    <Modal opened={opened} onClose={onClose} title={t("raiseException.title")} size="md">
      <Box component="form" onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="sm">
          <Select
            label={t("raiseException.itemSelect")}
            data={itemOptions}
            {...form.getInputProps("itemSelection")}
          />

          <Select
            label={t("raiseException.exceptionType")}
            data={EXCEPTION_TYPE_OPTIONS}
            required
            {...form.getInputProps("exceptionType")}
          />

          <Group grow>
            <NumberInput
              label={t("raiseException.expectedQuantity")}
              min={0}
              placeholder={t("raiseException.quantityPlaceholder")}
              {...form.getInputProps("expectedQuantity")}
            />
            <NumberInput
              label={t("raiseException.actualQuantity")}
              min={0}
              placeholder={t("raiseException.quantityPlaceholder")}
              {...form.getInputProps("actualQuantity")}
            />
          </Group>

          <Select
            label={t("raiseException.condition")}
            data={CONDITION_OPTIONS}
            placeholder={t("raiseException.conditionPlaceholder")}
            clearable
            {...form.getInputProps("conditionObserved")}
          />

          <Textarea
            label={t("raiseException.description")}
            placeholder={t("raiseException.descriptionPlaceholder")}
            required
            rows={3}
            {...form.getInputProps("description")}
          />

          {mutations.error && (
            <Text fz={14} fw={600}>
              {translateBackendError(mutations.error)}
            </Text>
          )}

          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={onClose}>
              {t("raiseException.cancel")}
            </Button>
            <Button type="submit" loading={mutations.submitting}>
              {t("raiseException.submit")}
            </Button>
          </Group>
        </Stack>
      </Box>
    </Modal>
  )
}
